/**
 * Landing Page - Mobile App Launch
 *
 * Marketing landing page matching mobile app design system:
 * - Dark gradient background
 * - Mobile-first responsive
 * - Onboarding flow showcase
 * - Premium scroll animations
 */

import { useEffect, useState } from 'react';
import Header from '../components/Header';
import { MetaTags } from '../components/SEO';
import { trackAppStoreClick, appStoreUrl } from '../utils/analytics';
import './Landing.css';

/** Real user reviews — one stage, Yael primary; user switches through. Founder-authored
    quotes (the co-founders) were removed 2026-10-06: a founder's words shown as a user
    review is an undisclosed material connection (FTC endorsement guides). */
const REVIEWS = [
  { name: 'Yael', quote: 'My commute is 28–30 minutes depending on the day. CueSesh consistently nails it. I finish an episode right as I’m parking. It sounds small, but it’s become one of my favorite parts of the day.' },
  { name: 'Roman', quote: 'I didn’t realize how much difference speed optimization could make. CueSesh figured out my comfortable pace during onboarding, and now everything I listen to just sounds ‘right’ — faster, but not rushed.' },
  { name: 'Danny', quote: 'Because the sessions line up with the time I have, I’m way more consistent with my listening. I’m getting through more content without feeling overwhelmed, and it’s helped me stay connected to the creators I love.' },
  { name: 'Sharon', quote: 'I love podcasts, but scrolling through endless feeds was draining. CueSesh feels curated and intentional. It’s like someone finally designed a listening experience for real life instead of assuming I always have two hours free.' },
];

export default function Landing() {

  // One review on stage at a time (Yael first); arrows/dots switch, no autoplay.
  const [quoteIdx, setQuoteIdx] = useState(0);
  // Product loop: tapping the video (or Enter/Space on it) pauses and resumes it —
  // the pause mechanism WCAG 2.2.2 asks for, without drawing a control (Alex's call).
  const [loopPlaying, setLoopPlaying] = useState(true);
  const toggleLoop = (v) => {
    if (v.paused) { v.play().catch(() => {}); setLoopPlaying(true); }
    else { v.pause(); setLoopPlaying(false); }
  };
  const stepQuote = (dir) => setQuoteIdx((i) => (i + dir + REVIEWS.length) % REVIEWS.length);

  useEffect(() => {
    // ====== INTERSECTION OBSERVER FOR SCROLL ANIMATIONS ======

    /**
     * Create intersection observer for fade-in animations
     * Uses a subtle slide-up effect with opacity transition
     */
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: 0.15 // Trigger when 15% of element is visible
    };

    const observerCallback = (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          // Add visible class with delay based on data-delay attribute
          const delay = entry.target.dataset.delay || 0;
          setTimeout(() => {
            entry.target.classList.add('animate-visible');
          }, delay);

          // Stop observing once animated
          observer.unobserve(entry.target);
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, observerOptions);

    // Hero text/CTA render immediately — no JS-added fade. (Adding the class
    // post-mount flashed the content visible → hidden → fade on load.)
    // The device cluster keeps its CSS-only entrance.

    // ====== SCROLL-TRIGGERED ANIMATIONS ======

    // Section titles
    const sectionTitles = document.querySelectorAll('.landing-section-title');
    sectionTitles.forEach(title => {
      title.classList.add('animate-fade');
      observer.observe(title);
    });


    // Moment stills - staggered entrance
    const bentoCards = document.querySelectorAll('.landing-moment');
    bentoCards.forEach((card, index) => {
      card.classList.add('animate-slide-up');
      card.dataset.delay = index * 120; // Stagger by 120ms
      observer.observe(card);
    });

    // Onboarding steps render static — no scroll entrance. The screenshots
    // fading in on scroll read as a glitchy load on mobile.

    // Footer fade in
    const footer = document.querySelector('.landing-footer');
    if (footer) {
      footer.classList.add('animate-fade');
      observer.observe(footer);
    }

    // ====== CONTINUITY-LINE SCROLL ======
    const brandLine = document.querySelector('.brand-continuity-line');
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    let ticking = false;

    const updateParallax = () => {
      const scrolled = window.pageYOffset;

      // Continuity line contracts from the edges inward as the page scrolls down
      if (brandLine && !reduceMotion) {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const progress = max > 0 ? Math.min(1, Math.max(0, scrolled / max)) : 0;
        brandLine.style.transform = `scaleX(${Math.max(0.04, 1 - progress)})`;
      }

      ticking = false;
    };

    const handleScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(updateParallax);
        ticking = true;
      }
    };

    // Drive the continuity-line contraction on scroll
    window.addEventListener('scroll', handleScroll);
    updateParallax();

    // ====== CLEANUP ======
    return () => {
      window.removeEventListener('scroll', handleScroll);
      observer.disconnect();
    };
  }, []);

  return (
    <>
      {/* SEO Meta Tags */}
      <MetaTags
        title="CueSesh - Listen like you live."
        description="CueSesh is a podcast app that fits your listening to the time you actually have. Free on the App Store."
        ogTitle="CueSesh - Listen like you live."
        ogDescription="CueSesh curates the podcasts that fit your time. Free on the App Store."
        ogImage="https://cuesesh.com/og-image.png"
        ogUrl="https://cuesesh.com/"
        twitterTitle="CueSesh - Listen like you live."
        twitterDescription="CueSesh curates the podcasts that fit your time. Free on the App Store."
        twitterImage="https://cuesesh.com/twitter-card.png"
        canonicalUrl="https://cuesesh.com/"
      />

      <div className="landing">
        {/* Brand continuity line — same hairline that crowns every app screen */}
        <div className="brand-continuity-line" aria-hidden="true" />

        {/* Background */}
        <div className="landing-bg" />

        {/* Header */}
        <Header />

      {/* Content */}
      <div className="landing-content">

        {/* Hero Section */}
        <section className="landing-hero landing-hero--photo">
          {/* One person in a commute moment, full-bleed (2026-09-28). The fold is
              LIGHT: dark copy over a warm-cream scrim on the left, the still on the
              right. Source: CueSesh-content/_source/stills/hero/hero-09.png (4K);
              the phone gets a portrait crop of the same frame. */}
          <picture className="landing-hero-photo">
            <source media="(max-width: 968px)" srcSet="/hero-commute-phone.jpg" />
            <img
              src="/hero-commute-1600.jpg"
              srcSet="/hero-commute-1600.jpg 1600w, /hero-commute-2880.jpg 2880w"
              sizes="100vw"
              alt="A woman wearing earbuds smiles out of a sunlit train window at a city skyline"
              fetchPriority="high"
              decoding="async"
            />
          </picture>
          <div className="landing-hero-scrim" aria-hidden="true" />

          <div className="landing-hero-container">
            <div className="landing-hero-left">
              <h1 className="landing-headline">
                Listen like you live.
              </h1>

              <p className="landing-description">
                CueSesh curates the podcasts that fit your time.
              </p>
              <p className="landing-hero-moments">For the commute, the run, the drive home.</p>

              {/* Hero CTA — the App Store badge is the fold's action; the header badge
                  stays as the persistent one further down the page. */}
              <a
                href={appStoreUrl('hero')}
                target="_blank"
                rel="noopener noreferrer"
                className="landing-app-store-link landing-hero-badge"
                onClick={() => trackAppStoreClick('hero')}
              >
                <img
                  src="/app-store-badge.svg"
                  alt="Download on the App Store"
                  className="landing-app-store-badge"
                />
              </a>
              <p className="landing-hero-free">Free. For iPhone.</p>
            </div>
          </div>
        </section>

        {/* How it works — one cream beat: the specific hook, the canon two-liner, the three
            things you give it, and the real product (Today's Session). Replaces the
            page-break line, the four benefit cards, the three-phone steps, the interactive
            act and the feature carousel (2026-10-06): the story told once, not four times. */}
        <section id="how-it-works" className="landing-how">
          <div className="landing-how-inner">
            <div className="landing-how-copy">
              <h2 className="landing-how-title">Got 48 minutes? CueSesh builds the perfect 48.</h2>
              <p className="landing-how-lead">
                CueSesh starts with one question: how much time do you have?
              </p>
              <ol className="landing-how-steps">
                <li>
                  <span className="landing-how-n">01</span>
                  <div><h3>Your time.</h3><p>Tell CueSesh how long you've got. The session lands on time.</p></div>
                </li>
                <li>
                  <span className="landing-how-n">02</span>
                  <div><h3>Your interests.</h3><p>Pick the topics you care about.</p></div>
                </li>
                <li>
                  <span className="landing-how-n">03</span>
                  <div><h3>Your pace.</h3><p>You set the top speed, 1x if you like. It never plays faster than sounds right to you.</p></div>
                </li>
              </ol>
            </div>
            <div className="landing-how-stage">
              {/* The product doing its one thing (2026-10-06): a muted 9s loop recorded
                  from the real app on the simulator — the time slider lands on 48, the
                  bloom curates, Today's Session arrives. Poster = the real 48:00 session
                  screenshot, which is also what reduced-motion users see (the effect
                  below pauses the video for them). */}
              <div className="landing-phone landing-how-phone">
                <video
                  className="landing-phone-screenshot landing-how-loop"
                  ref={(el) => {
                    if (!el) return;
                    // React reflects `muted` as a property only; iOS Safari's autoplay
                    // policy wants the attribute too.
                    el.muted = true; el.defaultMuted = true; el.setAttribute('muted', '');
                    // Reduced motion: hold the poster frame instead of playing.
                    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
                      el.removeAttribute('autoplay'); el.pause(); setLoopPlaying(false);
                    }
                  }}
                  autoPlay
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  poster="/how-session.jpg"
                  tabIndex={0}
                  role="button"
                  aria-pressed={!loopPlaying}
                  aria-label={`CueSesh in use: the time slider is set to 48 minutes, the session is curated, and Today's Session appears with episodes that fit. ${loopPlaying ? 'Press to pause.' : 'Paused. Press to play.'}`}
                  onClick={(e) => toggleLoop(e.currentTarget)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleLoop(e.currentTarget); } }}
                >
                  <source src="/how-loop.mp4" type="video/mp4" />
                </video>
              </div>
            </div>
          </div>
        </section>

        {/* Moments — three bounded windows, shot in the hero's light (2026-10-06).
            Captions carry a real number each so the strip says what the app is for,
            not just who uses it. Full-bleed three-up on desktop, stacked on phones. */}
        <section className="landing-moments" aria-label="Three moments CueSesh fits">
          <figure className="landing-moment">
            <img
              src="/moment-there-1000.jpg"
              srcSet="/moment-there-600.jpg 600w, /moment-there-1000.jpg 1000w"
              sizes="(max-width: 968px) 100vw, 34vw"
              alt="A man wearing earbuds steps off a commuter train onto a sunlit platform, the city skyline beyond"
              loading="lazy"
              decoding="async"
            />
            <figcaption>The way there. <span>24 min</span></figcaption>
          </figure>
          <figure className="landing-moment">
            <img
              src="/moment-home-1000.jpg"
              srcSet="/moment-home-600.jpg 600w, /moment-home-1000.jpg 1000w"
              sizes="(max-width: 968px) 100vw, 34vw"
              alt="A woman walks home along a tree-lined brownstone street at golden hour"
              loading="lazy"
              decoding="async"
            />
            <figcaption>The long way home. <span>41 min</span></figcaption>
          </figure>
          <figure className="landing-moment">
            <img
              src="/moment-run-1000.jpg"
              srcSet="/moment-run-600.jpg 600w, /moment-run-1000.jpg 1000w"
              sizes="(max-width: 968px) 100vw, 34vw"
              alt="A woman runs along a riverside path with the Manhattan skyline across the water"
              loading="lazy"
              decoding="async"
            />
            <figcaption>The run. <span>32 min</span></figcaption>
          </figure>
        </section>

        {/* FAQ — on the cream after the strip, before the close: for searchers (FAQPage
            JSON-LD), not the scroll. Native details = keyboard-accessible, no JS. */}
        <section id="faq" className="landing-faq">
          <h2 className="landing-faq-title">Questions</h2>
          <div className="landing-faq-list">
            <details className="landing-faq-item">
              <summary className="landing-faq-q">What is CueSesh?</summary>
              <p className="landing-faq-a">A podcast app built around your time. Tell it how many minutes you have and it curates a session of full episodes that lands on time.</p>
            </details>
            <details className="landing-faq-item">
              <summary className="landing-faq-q">How does CueSesh fit podcasts to the time I have?</summary>
              <p className="landing-faq-a">You set your topics and your maximum comfortable speed. CueSesh picks episodes that fit the window and paces playback so they land on time.</p>
            </details>
            <details className="landing-faq-item">
              <summary className="landing-faq-q">Does it work with video podcasts?</summary>
              <p className="landing-faq-a">Yes, where a show offers video. Every video episode can also play as audio, so your session works with the screen off.</p>
            </details>
            <details className="landing-faq-item">
              <summary className="landing-faq-q">Is CueSesh free?</summary>
              <p className="landing-faq-a">Yes. CueSesh is free to download on the App Store.</p>
            </details>
          </div>
          <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: "{\"@context\": \"https://schema.org\", \"@type\": \"FAQPage\", \"@id\": \"https://cuesesh.com/#faq\", \"mainEntity\": [{\"@type\": \"Question\", \"name\": \"What is CueSesh?\", \"acceptedAnswer\": {\"@type\": \"Answer\", \"text\": \"A podcast app built around your time. Tell it how many minutes you have and it curates a session of full episodes that lands on time.\"}}, {\"@type\": \"Question\", \"name\": \"How does CueSesh fit podcasts to the time I have?\", \"acceptedAnswer\": {\"@type\": \"Answer\", \"text\": \"You set your topics and your maximum comfortable speed. CueSesh picks episodes that fit the window and paces playback so they land on time.\"}}, {\"@type\": \"Question\", \"name\": \"Does it work with video podcasts?\", \"acceptedAnswer\": {\"@type\": \"Answer\", \"text\": \"Yes, where a show offers video. Every video episode can also play as audio, so your session works with the screen off.\"}}, {\"@type\": \"Question\", \"name\": \"Is CueSesh free?\", \"acceptedAnswer\": {\"@type\": \"Answer\", \"text\": \"Yes. CueSesh is free to download on the App Store.\"}}]}" }} />
        </section>

        {/* The close — one midnight beat: a real listener's words set up the headline
            (which is lifted from Yael's review), then the badge. The review stage is the
            carousel (Yael primary); arrows + dots switch, user-driven only. */}
        <section id="download-section" className="landing-bottom-cta">
        <div className="landing-testimonials">
          <figure className="landing-quote-lead" key={quoteIdx} aria-live="polite">
            <blockquote className="landing-quote-lead-text">
              “{REVIEWS[quoteIdx].quote}”
            </blockquote>
            <figcaption className="landing-quote-author">
              <span className="landing-quote-name">{REVIEWS[quoteIdx].name}</span>
            </figcaption>
          </figure>
          {/* One compact control row: ‹ dots › — the arrows sit with the dots they
              belong to instead of floating at the stage edges on wide screens. */}
          <div className="landing-quote-controls">
            <button type="button" className="landing-quote-nav" aria-label="Previous review" onClick={() => stepQuote(-1)}>‹</button>
            <div className="landing-quote-dots">
              {REVIEWS.map((r, i) => (
                <button
                  type="button"
                  key={r.name}
                  className={`landing-quote-dot${i === quoteIdx ? ' active' : ''}`}
                  aria-label={`Review from ${r.name}`}
                  aria-current={i === quoteIdx}
                  onClick={() => setQuoteIdx(i)}
                />
              ))}
            </div>
            <button type="button" className="landing-quote-nav" aria-label="Next review" onClick={() => stepQuote(1)}>›</button>
          </div>
        </div>
          <div className="landing-bottom-cta-inner">
            <img src="/cuesesh-logo.svg" alt="" aria-hidden="true" className="landing-bottom-cta-mark" />
            <h2 className="landing-bottom-cta-heading">Your next favorite part of the day.</h2>
            <p className="landing-bottom-cta-subheading">
              Download CueSesh on iPhone and start your first session.
            </p>

            {/* One row: the badge (primary) and, on desktop only, a small QR beside it —
                the only path a laptop visitor can actually complete. */}
            <div className="landing-bottom-cta-actions">
              <a
                href={appStoreUrl('bottom')}
                target="_blank"
                rel="noopener noreferrer"
                className="landing-app-store-link landing-bottom-cta-badge"
                onClick={() => trackAppStoreClick('bottom')}
              >
                <img
                  src="/app-store-badge.svg"
                  alt="Download on the App Store"
                  className="landing-app-store-badge"
                />
              </a>
              <div className="landing-bottom-cta-qr">
                <div className="landing-qr-code">
                  <img
                    src="/app-store-qr.png"
                    alt="Scan to download CueSesh"
                    className="landing-qr-image"
                  />
                </div>
              </div>
            </div>

          </div>
        </section>

        {/* Footer */}
        <footer className="landing-footer">
          <div className="landing-footer-social">
            <a href="https://facebook.com/cuesesh" target="_blank" rel="noopener noreferrer" className="landing-footer-social-link" aria-label="CueSesh on Facebook">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
              </svg>
            </a>
            <a href="https://x.com/cuesesh" target="_blank" rel="noopener noreferrer" className="landing-footer-social-link" aria-label="CueSesh on X">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
              </svg>
            </a>
            <a href="https://instagram.com/cueseshapp" target="_blank" rel="noopener noreferrer" className="landing-footer-social-link" aria-label="CueSesh on Instagram">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
                <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
                <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
              </svg>
            </a>
            <a href="https://www.tiktok.com/@cuesesh" target="_blank" rel="noopener noreferrer" className="landing-footer-social-link" aria-label="CueSesh on TikTok">
              <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">
                <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5"/>
              </svg>
            </a>
          </div>
          <div className="landing-footer-links">
            <a href="/privacy" className="landing-footer-link">Privacy</a>
            <a href="/terms" className="landing-footer-link">Terms</a>
            <a href="/contact" className="landing-footer-link">Contact</a>
          </div>
          <div className="landing-footer-legal">
            <p className="landing-footer-legal-line">
              CueSesh organizes publicly available podcast data from open RSS feeds. We do not host or redistribute content — audio and video are streamed directly from publishers.
            </p>
            <p className="landing-footer-legal-line">
              © 2026 CueSesh. All rights reserved.
            </p>
          </div>
        </footer>
      </div>
      </div>
    </>
  );
}